import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/features/toast";
import { useFilePickerStore } from "../store/file-picker-store";
import { usePickerUpload } from "./use-picker-upload";

const hasFiles = (event: React.DragEvent) => Array.from(event.dataTransfer.types).includes("Files");

/**
 * Seçici açıkken pencerenin herhangi bir yerine bırakılan ya da panodan yapıştırılan
 * dosyaları yükler. Panoda dosya yoksa yapıştırma olduğu gibi metin alanına gider.
 */
export const usePickerDropPaste = () => {
  const { t } = useTranslation();
  const { info: toastInfo } = useToast();
  const isOpen = useFilePickerStore((state) => state.isOpen);
  const { uploadFiles } = usePickerUpload();
  const [isDragging, setIsDragging] = useState(false);
  const dragDepthRef = useRef(0);

  const resetDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetDrag();
      return;
    }
    const handlePaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      toastInfo(t("filePicker.pastedFromClipboard", { count: files.length }));
      void uploadFiles(files);
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isOpen, resetDrag, t, toastInfo, uploadFiles]);

  const dropHandlers = {
    onDragEnter: (event: React.DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    },
    onDragOver: (event: React.DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (event: React.DragEvent) => {
      if (!hasFiles(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragging(false);
    },
    onDrop: (event: React.DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      resetDrag();
      void uploadFiles(event.dataTransfer.files);
    },
  };

  return { isDragging, dropHandlers };
};
